import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import DirectiveCards from '../../components/DirectiveCards';
import FieldUpdateBanner from '../../components/FieldUpdateBanner';
import { getAllBoards, getBoardMembers, isMockMode } from '../../services/api';
import { loadEmployees } from '../../services/employeeData';
import { apiFetch } from '../../services/apiClient';
import { isMobileDevice, preferredView } from '../../services/viewMode';
import ExecOverview from './ExecOverview';
import OfficeDashboard from './OfficeDashboard';
import TimeOffModal from '../TimeOffModal';
import { CurrentActivityPill } from '../../components/CurrentActivity';
import { MyReminders } from '../../components/Reminders';

function getSessionRole() { try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.role || 'admin'; } catch { return 'admin'; } }

export default function AdminDashboard() {
  const navigate = useNavigate();
  const role = getSessionRole();
  const isExec = role === 'Executive' || role === 'admin' || role === 'Admin';
  const isOffice = role === 'Accounting';   // the office gets its own six tiles (Pat, Oct 9 2026)

  // On a phone, drop mobile-capable users (Executive/PM/Accounting/…) into the
  // mobile home instead of the cramped desktop dashboard — e.g. when the PWA
  // reopens to /admin. Respects an explicit "Desktop view" choice (saved pref).
  useEffect(() => {
    if (role !== 'admin' && isMobileDevice() && preferredView() === 'mobile') {
      navigate('/dashboard', { replace: true });
    }
  }, [role, navigate]);
  const [boards, setBoards] = useState([]);
  const [stats, setStats] = useState({ boards: 0, members: 0, cards: 0 });
  const [loading, setLoading] = useState(true);
  const [recentActivity] = useState([
    { id: 1, text: 'Employee portal accessed', time: '2 min ago', type: 'access' },
    { id: 2, text: 'Admin signed in', time: '5 min ago', type: 'auth' },
  ]);
  const [permits, setPermits] = useState({ overdue: 0, soon: 0 });
  // Executives and the office don't clock in, so the time clock's Request Time Off never
  // reaches them — this is the same request (same approval, same Sunday schedule note).
  // The owner login ('admin') isn't a person on the employee list, so it has nobody to
  // ask for.
  const [showTimeOff, setShowTimeOff] = useState(false);
  const canRequestTimeOff = role !== 'admin';

  useEffect(() => {
    loadStats();
    apiFetch('/permits-licenses').then(list => {
      const now = new Date(); now.setHours(0, 0, 0, 0);
      let overdue = 0, soon = 0;
      (Array.isArray(list) ? list : []).forEach(it => {
        if (!it.due_date) return;
        const d = new Date(it.due_date + 'T00:00:00'); if (isNaN(d)) return;
        const days = Math.round((d - now) / 86400000);
        if (days < 0) overdue++; else if (days <= 30) soon++;
      });
      setPermits({ overdue, soon });
    }).catch(() => {});
  }, []);

  async function loadStats() {
    try {
      const b = await getAllBoards();
      setBoards(b);
      let totalMembers = 0;
      await Promise.all(b.slice(0, 5).map(async board => {
        try {
          const m = await getBoardMembers(board.id);
          totalMembers += m.length;
        } catch {}
      }));
      setStats({ boards: b.length, members: totalMembers, cards: '—' });
    } catch {}
    finally { setLoading(false); }
  }

  const employees = loadEmployees();

  return (
    <AdminLayout>
      {isMockMode() && (
        <div className="mock-banner">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.3" fill="none"/><path d="M7 4v3M7 9.5v.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
          Demo mode — showing sample project data. Add Trello credentials to <code>.env</code> to connect live boards.
        </div>
      )}
      {/* Priorities sent to the person signed in — nothing renders when none are waiting (Ryan, Sep 30 2026). */}
      {/* A weekly update sent to them and not read yet — blue, above the red (Pat, Oct 5 2026). */}
      <FieldUpdateBanner />
      <DirectiveCards />
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Dashboard</h1>
          <p className="admin-page-sub">Welcome back, Admin · {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {/* What everyone in the office is on — each line opens the thing itself. */}
          <CurrentActivityPill mine />
          {canRequestTimeOff && (
            <button onClick={() => setShowTimeOff(true)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 16px', borderRadius: 999, border: '1px solid #c7d2fe', background: '#eef2ff', color: '#3730a3', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
              <span>🌴</span> Request Time Off
            </button>
          )}
        </div>
      </div>
      {showTimeOff && <TimeOffModal centered onClose={() => setShowTimeOff(false)} />}
      {/* ⏰ My Reminders (Ryan, Oct 26 2026). */}
      <div style={{ margin: '0 0 20px', maxWidth: 640 }}><MyReminders /></div>

      {(permits.overdue > 0 || permits.soon > 0) && (
        <div onClick={() => window.location.href = '/admin/permits'}
          style={{ margin: '0 0 20px', background: permits.overdue > 0 ? '#fef2f2' : '#fff7ed', border: `1.5px solid ${permits.overdue > 0 ? '#fecaca' : '#fed7aa'}`, borderRadius: 12, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer' }}>
          <span style={{ fontSize: 24 }}>📜</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: permits.overdue > 0 ? '#991b1b' : '#9a3412' }}>Permits &amp; Licenses need attention</div>
            <div style={{ fontSize: 13, color: permits.overdue > 0 ? '#b91c1c' : '#c2410c' }}>
              {permits.overdue > 0 && `${permits.overdue} overdue`}
              {permits.overdue > 0 && permits.soon > 0 && ' · '}
              {permits.soon > 0 && `${permits.soon} due within 30 days`}
            </div>
          </div>
          <span style={{ color: permits.overdue > 0 ? '#dc2626' : '#f97316', fontSize: 18 }}>→</span>
        </div>
      )}

      {isExec && <ExecOverview />}
      {isOffice && <OfficeDashboard />}

      {!isExec && !isOffice && (<>
      <div className="admin-stat-grid">
        <div className="admin-stat-card">
          <div className="admin-stat-icon orange">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <rect x="2" y="2" width="6" height="16" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none"/>
              <rect x="11" y="2" width="7" height="10" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            </svg>
          </div>
          <div>
            <div className="admin-stat-val">{loading ? '…' : stats.boards}</div>
            <div className="admin-stat-label">Active boards</div>
          </div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-icon dark">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="6" r="3.5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
              <path d="M2 18c0-4 3.6-7 8-7s8 3 8 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
            </svg>
          </div>
          <div>
            <div className="admin-stat-val">{employees.length}</div>
            <div className="admin-stat-label">Employees</div>
          </div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-icon orange">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <rect x="2" y="2" width="16" height="16" rx="3" stroke="currentColor" strokeWidth="1.5" fill="none"/>
              <path d="M6 10h8M6 7h5M6 13h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
          <div>
            <div className="admin-stat-val">{loading ? '…' : stats.members}</div>
            <div className="admin-stat-label">Board members</div>
          </div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-icon dark">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.5" fill="none"/>
              <path d="M10 6v4l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
          <div>
            <div className="admin-stat-val">—</div>
            <div className="admin-stat-label">T-Sheets hours</div>
          </div>
        </div>
      </div>

      <div className="admin-two-col">
        {/* (The Trello boards card was here — Trello is not used, Oct 6 2026.) */}

        {/* Recent activity */}
        <div className="admin-card">
          <div className="admin-card-header">
            <h2 className="admin-card-title">Recent activity</h2>
          </div>
          {recentActivity.map(a => (
            <div key={a.id} className="admin-list-row">
              <div className={`admin-list-icon ${a.type === 'auth' ? 'orange' : ''}`}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.3" fill="none"/>
                  <path d="M7 4v3l2 2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                </svg>
              </div>
              <div className="admin-list-text">
                <span className="admin-list-primary">{a.text}</span>
                <span className="admin-list-secondary">{a.time}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
      </>)}
    </AdminLayout>
  );
}
